
import React, { useRef, useCallback, ReactNode } from 'react';
import { motion, PanInfo, useDragControls } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';

interface GestureHandlerProps {
  children: ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onLongPress?: () => void;
  className?: string;
  disabled?: boolean;
  allowScroll?: boolean; // New prop to allow scroll passthrough
}

export const GestureHandler: React.FC<GestureHandlerProps> = ({
  children,
  onSwipeLeft,
  onSwipeRight,
  onLongPress,
  className,
  disabled = false,
  allowScroll = true // Default to allow scroll
}) => {
  const isMobile = useIsMobile();
  const dragControls = useDragControls();
  const longPressTimer = useRef<NodeJS.Timeout>();
  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const isScrolling = useRef(false);

  const handlePanStart = useCallback((event: any, info: PanInfo) => {
    if (disabled || !isMobile) return;
    
    isDragging.current = false;
    isScrolling.current = false;
    startPos.current = { x: info.point.x, y: info.point.y };
    
    // Start long press timer
    if (onLongPress) {
      longPressTimer.current = setTimeout(() => {
        if (!isDragging.current && !isScrolling.current) {
          onLongPress();
        }
      }, 500);
    }
  }, [disabled, isMobile, onLongPress]);

  const handlePan = useCallback((event: any, info: PanInfo) => {
    if (disabled || !isMobile) return;
    
    const deltaX = Math.abs(info.point.x - startPos.current.x);
    const deltaY = Math.abs(info.point.y - startPos.current.y);
    
    // If vertical movement is more prominent, consider it scrolling
    if (allowScroll && deltaY > deltaX && deltaY > 15) {
      isScrolling.current = true;
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
      return;
    }
    
    // If movement is significant horizontally, it's a gesture
    if (deltaX > 10 || deltaY > 10) {
      isDragging.current = true;
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    }
  }, [disabled, isMobile, allowScroll]);

  const handlePanEnd = useCallback((event: any, info: PanInfo) => {
    if (disabled || !isMobile) return;
    
    // Clear long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
    
    // Don't process swipes if we were scrolling
    if (isScrolling.current) {
      isDragging.current = false;
      isScrolling.current = false;
      return;
    }
    
    // Check for swipe gestures
    const deltaX = info.point.x - startPos.current.x;
    const deltaY = Math.abs(info.point.y - startPos.current.y);
    const velocity = Math.abs(info.velocity.x);
    
    // Swipe detection: minimum distance, velocity, and horizontal movement
    if (Math.abs(deltaX) > 100 && velocity > 300 && deltaY < 50) {
      if (deltaX > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (deltaX < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    }
    
    isDragging.current = false;
    isScrolling.current = false;
  }, [disabled, isMobile, onSwipeLeft, onSwipeRight]);

  // If not mobile or gestures disabled, return children without gesture handling
  if (!isMobile || disabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      drag={false}
      dragControls={dragControls}
      onPanStart={handlePanStart}
      onPan={handlePan}
      onPanEnd={handlePanEnd}
      style={{ 
        // Allow vertical scrolling but handle horizontal gestures
        touchAction: allowScroll ? 'pan-y' : 'none',
        // Don't block pointer events for scrolling
        pointerEvents: 'auto'
      }}
    >
      {children}
    </motion.div>
  );
};

// Hook for gesture shortcuts
export const useGestureShortcuts = () => {
  const isMobile = useIsMobile();
  
  const createGestureProps = useCallback((
    onSwipeLeft?: () => void,
    onSwipeRight?: () => void,
    onLongPress?: () => void
  ) => {
    if (!isMobile) return {};
    
    return {
      onSwipeLeft,
      onSwipeRight,
      onLongPress
    };
  }, [isMobile]);

  return { createGestureProps, isMobile };
};
